import React, { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from '../api/axiosInstance';
import { Container, Spinner } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../stores/authStore";
import useDiaryStore from "../stores/diaryStore";
import { Viewer } from "@toast-ui/react-editor";
import "@toast-ui/editor/dist/toastui-editor-viewer.css";

// API 엔드포인트
const API_URL = "posts";
const ITEMS_PER_PAGE = 12;

const DiaryBoard = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const isAdmin = user?.role === 'admin';

    // Zustand Store
    const {
        posts, addPosts,
        page, setPage,
        hasMore, setHasMore,
        setScrollPosition
    } = useDiaryStore();

    const [loading, setLoading] = useState(false);
    const observer = useRef();

    // 게시글 목록 조회
    const fetchPosts = useCallback(async (pageNum) => {
        if (loading) return;
        setLoading(true);
        try {
            const response = await axiosInstance.get(`${API_URL}?board_id=2&page=${pageNum}`);
            const apiData = response.data.data;

            if (apiData?.postList && apiData.postList.length > 0) {
                // 중복 제거 후 스토어 업데이트
                const newPosts = apiData.postList;

                // Zustand 스토어 업데이트 (함수형 업데이트 대신 직접 로직 처리)
                // 여기서는 기존 posts를 참조해야 하므로 store의 값을 사용하거나
                // setPosts/addPosts 로직을 스토어 내부에서 처리하는 것이 좋음.
                // 하지만 간단하게 여기서 필터링 후 추가.

                // useDiaryStore.getState().posts 를 사용하여 최신 상태 참조 가능하지만
                // 컴포넌트 리렌더링 시 posts가 업데이트 되므로 posts를 의존성에 넣거나
                // 스토어 액션을 잘 활용해야 함.

                // addPosts 액션이 단순히 추가만 하므로, 중복 체크는 여기서 수행
                const currentPosts = useDiaryStore.getState().posts;
                const filteredPosts = newPosts.filter(p => !currentPosts.some(existing => existing.json.id === p.json.id));

                if (filteredPosts.length > 0) {
                    addPosts(filteredPosts);
                }

                if (apiData.postList.length < ITEMS_PER_PAGE) {
                    setHasMore(false);
                }
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error fetching posts:", error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [loading, addPosts, setHasMore]);

    // 초기 로딩 및 스크롤 복원
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // 마운트 시: 데이터가 없으면 첫 페이지 로드, 있으면 스크롤 복원
        if (useDiaryStore.getState().posts.length === 0) {
            fetchPosts(1);
        } else {
            window.scrollTo(0, useDiaryStore.getState().scrollPosition);
        }

        // 언마운트 시: 현재 스크롤 위치 저장
        return () => {
            setScrollPosition(window.scrollY);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // 무한 스크롤 옵저버
    const lastPostElementRef = useCallback(node => {
        // 로딩 중이어도 observer는 연결하되, 콜백에서 차단 (loading이 풀리면 다시 연결되면서 트리거됨)
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                // 페이지 증가 및 요청
                const nextPage = page + 1;
                setPage(nextPage); // 스토어의 페이지 업데이트
                fetchPosts(nextPage);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore, fetchPosts, page, setPage]);

    // 랜덤 스타일 생성 함수 (메모이제이션)
    const getCardStyle = (id) => {
        const rotations = [-1, 1, -2, 2, -1.5, 1.5, 0];
        const colors = [
            '#fff9c4', // Soft Yellow
            '#f8bbd0', // Soft Pink
            '#bbdefb', // Soft Blue
            '#c8e6c9', // Soft Green
            '#e1bee7', // Soft Purple
            '#fff3e0'  // Soft Orange/Cream
        ];

        // ID를 숫자로 변환하여 인덱스 생성 (문자열일 경우 아스키 코드 합산)
        const idNum = typeof id === 'string'
            ? id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
            : id;

        return {
            transform: `rotate(${rotations[idNum % rotations.length]}deg)`,
            backgroundColor: colors[idNum % colors.length]
        };
    };

    // 랜덤 스팬 생성 함수
    const getSpanClass = (index) => {
        const patterns = [
            [3, 4, 5], [3, 5, 4], [4, 4, 4], [5, 3, 4],
            [5, 4, 3], [4, 3, 5], [4, 5, 3], [6, 3, 3],
            [3, 6, 3], [3, 3, 6]
        ];
        const patternIndex = Math.floor(index / 3) % patterns.length;
        const itemIndex = index % 3;
        const span = patterns[patternIndex][itemIndex];
        return `span-${span}`;
    };

    return (
        <>
            {/* 구글 폰트 및 커스텀 스타일 */}
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Gamja+Flower&family=Noto+Sans+KR:wght@300;400;500;700&family=Fira+Code:wght@500&display=swap');

                    :root {
                        --bg-color: #ffffff;
                        --card-border: transparent;
                        --text-main: #333;
                        --text-sub: #555;
                    }

                    html, body {
                        background-color: var(--bg-color);
                        font-family: 'Noto Sans KR', sans-serif;
                        overflow-x: hidden; /* 가로 스크롤 방지 */
                        width: 100%;
                        position: relative;
                    }

                    /* --- 배너 스타일 --- */
                    .dev-banner {
                        padding: 20px 0;
                        margin-bottom: 30px;
                        display: flex;
                        justify-content: flex-start;
                        align-items: center;
                        position: relative;
                        overflow: hidden;
                    }

                    .banner-content {
                        font-family: 'Fira Code', monospace;
                        font-size: clamp(1.5rem, 3vw, 2.5rem);
                        position: relative;
                        display: flex;
                        align-items: center;
                        opacity: 0;
                        animation: turn-on 2.5s linear forwards;
                        white-space: nowrap;
                    }

                    .syntax-object { color: #005cc5; text-shadow: 0 2px 4px rgba(0, 92, 197, 0.1); }
                    .syntax-dot, .syntax-semi { color: #24292e; opacity: 0.8; }
                    .syntax-method { color: #6f42c1; text-shadow: 0 2px 4px rgba(111, 66, 193, 0.1); }
                    .syntax-paren { color: #e36209; }
                    .syntax-string { 
                        color: #d73a49; 
                        font-weight: 600; 
                        text-shadow: 0 2px 10px rgba(215, 58, 73, 0.2);
                        animation: pulsate 2s infinite alternate 2.5s;
                    }
                    .cursor {
                        display: inline-block;
                        width: 14px;
                        height: 1em;
                        background-color: #24292e;
                        margin-left: 10px;
                        border-radius: 2px;
                        animation: blink 1s step-end infinite 2.5s;
                        opacity: 0;
                    }

                    @keyframes turn-on {
                        0% { opacity: 0; filter: blur(4px); }
                        5% { opacity: 0; filter: blur(4px); }
                        6% { opacity: 0.8; filter: blur(0px); }
                        7% { opacity: 0; filter: blur(2px); }
                        8% { opacity: 0.8; filter: blur(0px); }
                        9% { opacity: 0; filter: blur(2px); }
                        10% { opacity: 0.5; filter: blur(0px); }
                        11% { opacity: 0; }
                        12% { opacity: 0.9; }
                        15% { opacity: 0; }
                        20% { opacity: 0; }
                        21% { opacity: 1; transform: scale(1.02); }
                        22% { transform: scale(1); }
                        23% { opacity: 0; }
                        24% { opacity: 1; }
                        30% { opacity: 0; }
                        31% { opacity: 1; }
                        35% { opacity: 1; }
                        36% { opacity: 0.2; }
                        37% { opacity: 1; }
                        50% { opacity: 1; }
                        55% { opacity: 0.5; } 
                        57% { opacity: 0; }
                        60% { opacity: 1; }
                        70% { opacity: 0.8; }
                        80% { opacity: 1; }
                        90% { opacity: 0.95; }
                        100% { opacity: 1; filter: blur(0px); }
                    }
                    @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
                    @keyframes pulsate {
                        0% { transform: scale(1); text-shadow: 0 2px 10px rgba(215, 58, 73, 0.2); }
                        100% { transform: scale(1.02); text-shadow: 0 5px 15px rgba(215, 58, 73, 0.4); }
                    }

                    /* --- Masonry 그리드 (12컬럼 랜덤 레이아웃) --- */
                    .masonry-grid {
                        display: grid;
                        grid-template-columns: repeat(12, 1fr);
                        gap: 30px; /* 간격 조금 더 넓힘 */
                        padding: 20px;
                    }
                    
                    /* 데스크탑: 랜덤 너비 적용 */
                    @media (min-width: 992px) {
                        .span-3 { grid-column: span 3; }
                        .span-4 { grid-column: span 4; }
                        .span-5 { grid-column: span 5; }
                        .span-6 { grid-column: span 6; }
                    }

                    /* 태블릿: 2컬럼 (6칸씩) */
                    @media (max-width: 991px) {
                        .masonry-grid {
                            grid-template-columns: repeat(2, 1fr);
                        }
                        .graffiti-card { grid-column: auto !important; } /* span 클래스 무시 */
                    }

                    /* 모바일: 1컬럼 */
                    @media (max-width: 768px) {
                        .masonry-grid {
                            grid-template-columns: minmax(0, 1fr); /* 오버플로우 방지 */
                            padding: 0 10px; /* 좌우 패딩 대칭 적용 */
                            gap: 20px;
                        }
                        .graffiti-card {
                            transform: none !important; /* 모바일에서는 회전 제거하여 오버플로우 방지 */
                            margin: 0 auto; /* 카드 중앙 정렬 */
                            width: 100%; /* 가득 채우기 */
                        }
                    }

                    /* --- 포스트잇 카드 스타일 --- */
                    .graffiti-card {
                        border: none;
                        border-radius: 2px; /* 포스트잇은 각진 느낌 */
                        padding: 24px;
                        margin-bottom: 0;
                        box-shadow: 3px 3px 10px rgba(0,0,0,0.2); /* 그림자 강화 */
                        transition: transform 0.2s ease, box-shadow 0.2s ease;
                        position: relative;
                        overflow: visible;
                        cursor: pointer;
                        height: 100%;
                        display: flex;
                        flex-direction: column;
                        font-family: 'Noto Sans KR', sans-serif; /* 폰트 변경 */
                    }
                    .graffiti-card:hover {
                        transform: scale(1.02) !important; /* 호버 시 약간 확대 */
                        box-shadow: 5px 10px 25px rgba(0,0,0,0.25);
                        z-index: 10;
                    }
                    
                    /* 테이프 효과 */
                    .graffiti-card::before {
                        content: '';
                        position: absolute;
                        top: -12px;
                        left: 50%;
                        transform: translateX(-50%) rotate(-1deg);
                        width: 100px;
                        height: 35px;
                        background-color: rgba(255, 255, 255, 0.4);
                        box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                        z-index: 5;
                        backdrop-filter: blur(1px);
                        border: 1px solid rgba(255,255,255,0.2);
                    }

                    .pin {
                        display: none; /* 핀 제거 (테이프로 대체) */
                    }

                    .card-meta {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 16px;
                        padding-bottom: 10px;
                        border-bottom: 1px dashed rgba(0,0,0,0.1);
                    }
                    .date {
                        font-family: 'Noto Sans KR', sans-serif; /* 폰트 변경 */
                        font-size: 1rem;
                        color: var(--text-sub);
                        font-weight: 500;
                    }
                    .tag {
                        font-size: 0.9rem;
                        background: transparent;
                        padding: 0;
                        border: none;
                        color: #555;
                        font-family: 'Noto Sans KR', sans-serif; /* 폰트 변경 */
                    }

                    /* --- Toast UI 뷰어 스타일 재정의 --- */
                    .toastui-editor-contents {
                        font-size: 16px;
                        font-family: 'Noto Sans KR', sans-serif !important; /* 폰트 변경 */
                        max-height: 300px;
                        overflow: hidden;
                        position: relative;
                        flex-grow: 1;
                    }
                    /* 긴 내용에 대한 페이드 아웃 효과 */
                    .toastui-editor-contents::after {
                        content: '';
                        position: absolute;
                        bottom: 0;
                        left: 0;
                        width: 100%;
                        height: 50px;
                        background: linear-gradient(to bottom, transparent, rgba(255,255,255,0.5));
                        pointer-events: none;
                    }
                    .toastui-editor-contents p { color: #222; line-height: 1.5; }
                    .toastui-editor-contents img { border-radius: 2px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); margin-top: 10px; max-width: 100%; }
                    .toastui-editor-contents pre { background-color: rgba(0,0,0,0.05); border-radius: 4px; margin: 10px 0; }

                    /* --- 플로팅 액션 버튼 (FAB) --- */
                    .fab {
                        position: fixed;
                        bottom: 150px;
                        right: 15%;
                        width: 60px;
                        height: 60px;
                        background-color: #333;
                        color: white;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 24px;
                        box-shadow: 0 4px 15px rgba(0,0,0,0.3);
                        cursor: pointer;
                        transition: transform 0.2s;
                        z-index: 100;
                        border: none;
                    }
                    .fab:hover { transform: scale(1.1) rotate(90deg); }
                `}
            </style>

            <Container className="py-4" style={{ maxWidth: '1200px' }}>
                {/* 배너 */}
                <div className="dev-banner">
                    <div className="banner-content">
                        <span className="syntax-object">console</span>
                        <span className="syntax-dot">.</span>
                        <span className="syntax-method">log</span>
                        <span className="syntax-paren">(</span>
                        <span className="syntax-string">'Me'</span>
                        <span className="syntax-paren">)</span>
                        <span className="syntax-semi">;</span>
                        <span className="cursor"></span>
                    </div>
                </div>

                {/* Masonry 그리드 */}
                <div className="masonry-grid">
                    <AnimatePresence>
                        {posts.map((post, index) => {
                            const spanClass = getSpanClass(index);
                            const cardStyle = getCardStyle(post.json.id);

                            return (
                                <motion.div
                                    key={`${post.json.id}-${index}`}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: index % 3 * 0.1 }}
                                    className={`graffiti-card ${spanClass}`}
                                    style={cardStyle}
                                    whileHover={{ scale: 1.02, zIndex: 10, transition: { duration: 0.2 } }}
                                    onClick={() => navigate(`/diary/${post.json.id}`)}
                                >
                                    {/* 테이프는 CSS ::before로 처리됨 */}
                                    <div className="card-meta">
                                        <span className="date">{post.json.created_at?.substring(0, 10)}</span>
                                        <span className="tag">#{post.json.id}</span>
                                    </div>
                                    <div className="toastui-editor-contents">
                                        {/* 마크다운을 올바르게 렌더링하기 위해 Toast UI Viewer 사용 */}
                                        <Viewer initialValue={post.json.content} />
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </div>

                {/* 무한 스크롤 감지 센서 (Sentinel) */}
                {hasMore && (
                    <div ref={lastPostElementRef} style={{ height: '20px', margin: '20px 0' }} />
                )}

                {/* 로딩 및 빈 상태 */}
                {loading && (
                    <div className="text-center my-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                )}
                {!hasMore && posts.length >= 12 && (
                    <div className="text-center my-5 text-muted">
                        <p style={{ fontFamily: 'Gamja Flower', fontSize: '1.2rem' }}>더 이상 글이 없어요 📝</p>
                    </div>
                )}
                {!loading && posts.length === 0 && (
                    <div className="text-center my-5 text-muted">
                        <p style={{ fontFamily: 'Gamja Flower', fontSize: '1.5rem' }}>뭘 적어볼까...?</p>
                    </div>
                )}

                {/* 관리자 글쓰기 버튼 */}
                {isAdmin && (
                    <button className="fab" onClick={() => navigate('/diary/write')}>
                        +
                    </button>
                )}
            </Container>
        </>
    );
};

export default DiaryBoard;
